import {
    MAX_STRIPE_EXPRESS_LOGO_FILE_SIZE_BYTES,
    MIN_STRIPE_EXPRESS_LOGO_DIMENSION_PX,
    STRIPE_EXPRESS_DEFAULT_ACCENT_COLOR,
    STRIPE_EXPRESS_DEFAULT_BRAND_COLOR,
    STRIPE_EXPRESS_LOGO_MIME_TYPES,
    StripeExpressLogoMimeType,
} from "@common/constants/domains/stripe/stripe-express-branding";
import { normalizeHexColor } from "@common/helpers/color";
import {
    StripeExpressBranding,
    StripeExpressUpdateBrandingBody,
} from "@common/schemas/domains/stripe/stripe-express.schema";

export type StripeExpressBrandingLogoDraft =
    | { status: "unchanged" }
    | { status: "cleared" }
    | {
          status: "replaced";
          data: string;
          mimeType: StripeExpressLogoMimeType;
      };

export interface StripeExpressBrandingDraft {
    brandColor: string | null;
    accentColor: string | null;
    logo: StripeExpressBrandingLogoDraft;
}

type LogoFileCheck =
    | { mimeType: StripeExpressLogoMimeType }
    | { message: string };

export function isStripeExpressLogoMimeType(
    value: string,
): value is StripeExpressLogoMimeType {
    return STRIPE_EXPRESS_LOGO_MIME_TYPES.some(
        (mimeType) => mimeType === value,
    );
}

export function draftFromStripeExpressBranding(
    branding: StripeExpressBranding,
): StripeExpressBrandingDraft {
    return {
        brandColor: branding.brandColor,
        accentColor: branding.accentColor,
        logo: { status: "unchanged" },
    };
}

export function validateStripeExpressLogoFile(file: {
    type: string;
    size: number;
}): LogoFileCheck {
    if (!isStripeExpressLogoMimeType(file.type)) {
        return { message: "Upload a PNG or JPEG logo" };
    }
    if (file.size > MAX_STRIPE_EXPRESS_LOGO_FILE_SIZE_BYTES) {
        return { message: "Logo must be 512 KB or smaller" };
    }
    return { mimeType: file.type };
}

export function stripeExpressLogoDimensionsError(dimensions: {
    width: number;
    height: number;
}): string | null {
    if (
        dimensions.width < MIN_STRIPE_EXPRESS_LOGO_DIMENSION_PX ||
        dimensions.height < MIN_STRIPE_EXPRESS_LOGO_DIMENSION_PX
    ) {
        return `Logo must be at least ${MIN_STRIPE_EXPRESS_LOGO_DIMENSION_PX}px by ${MIN_STRIPE_EXPRESS_LOGO_DIMENSION_PX}px`;
    }
    return null;
}

export function logoDraftAfterRemove(
    logo: StripeExpressBrandingLogoDraft,
): StripeExpressBrandingLogoDraft {
    if (logo.status === "replaced") {
        return { status: "unchanged" };
    }
    return { status: "cleared" };
}

export function stripeExpressLogoPreviewUrl({
    savedLogoUrl,
    logo,
}: {
    savedLogoUrl: string | null;
    logo: StripeExpressBrandingLogoDraft;
}): string | null {
    if (logo.status === "cleared") {
        return null;
    }
    if (logo.status === "replaced") {
        return `data:${logo.mimeType};base64,${logo.data}`;
    }
    return savedLogoUrl;
}

export function buildStripeExpressBrandingUpdate({
    saved,
    draft,
}: {
    saved: StripeExpressBranding;
    draft: StripeExpressBrandingDraft;
}): StripeExpressUpdateBrandingBody {
    const body: StripeExpressUpdateBrandingBody = {};

    if (draft.logo.status === "cleared" && saved.logoUrl !== null) {
        body.logo = null;
    } else if (draft.logo.status === "replaced") {
        body.logo = {
            data: draft.logo.data,
            mimeType: draft.logo.mimeType,
        };
    }

    if (
        !isColorUnchanged({
            draftColor: draft.brandColor,
            savedColor: saved.brandColor,
            defaultColor: STRIPE_EXPRESS_DEFAULT_BRAND_COLOR,
        })
    ) {
        body.brandColor = draft.brandColor;
    }
    if (
        !isColorUnchanged({
            draftColor: draft.accentColor,
            savedColor: saved.accentColor,
            defaultColor: STRIPE_EXPRESS_DEFAULT_ACCENT_COLOR,
        })
    ) {
        body.accentColor = draft.accentColor;
    }

    return body;
}

function isColorUnchanged({
    draftColor,
    savedColor,
    defaultColor,
}: {
    draftColor: string | null;
    savedColor: string | null;
    defaultColor: string;
}): boolean {
    if (draftColor === null) {
        return savedColor === null;
    }
    return (
        normalizeHexColor(draftColor) ===
        normalizeHexColor(savedColor ?? defaultColor)
    );
}
